import React, { ReactElement } from 'react';
import { Modal as AntdModal } from 'antd';
import Button from 'app/atoms/Button';
import classNames from 'classnames';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { ButtonColorType } from 'antd/es/button';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  isOpen: boolean;
  title?: string;
  submitText?: string;
  onClose: () => void;
  children: React.ReactNode;
  onSubmit?: () => void;
  cancelText?: string;
  onCancel?: () => void;
  className?: string;
  submitLoading?: boolean;
  submitBtnSize?: SizeType;
  cancelBtnSize?: SizeType;
  cancelBtnColor?: ButtonColorType;
  allowSubmit?: boolean;
  dataTestid?: string;
  zIndex?: number;
  footer?: React.ReactNode;
  destroyOnClose?: boolean;
  closable?: boolean;
  confirmCloseMessage?: string;
  viewOnly?: boolean;
}

const Modal = ({
  isOpen,
  title,
  submitText,
  children,
  onClose,
  onSubmit,
  onCancel,
  cancelText = 'Cancel',
  className = '',
  submitLoading = false,
  submitBtnSize = 'large',
  cancelBtnSize,
  cancelBtnColor,
  allowSubmit = true,
  dataTestid = 'modal',
  zIndex = 1000,
  footer,
  destroyOnClose = false,
  closable = true,
  confirmCloseMessage,
  viewOnly = false,
}: ModalProps): ReactElement => {
  const { t } = useTranslation();

  const handleCloseWithConfirmation = () => {
    AntdModal.confirm({
      title: t('modal.title'),
      content: confirmCloseMessage ?? t('modal.confirmCloseMessage'),
      okText: t('okText'),
      cancelText: t('cancelText'),
      onOk: onClose,
      centered: true,
      zIndex: 1006,
    });
  };

  return (
    <AntdModal
      zIndex={zIndex}
      className={classNames('custom-modal', className)}
      open={isOpen}
      title={title && <p className="h3-bold text-primary-600">{title}</p>}
      onCancel={viewOnly ? onClose : handleCloseWithConfirmation}
      data-testid={dataTestid}
      destroyOnClose={destroyOnClose}
      maskClosable={false}
      footer={
        footer === null
          ? null
          : footer ||
            [
              onCancel && (
                <Button key="cancel" variant="outlined" size={cancelBtnSize} color={cancelBtnColor} onClick={onCancel}>
                  {cancelText}
                </Button>
              ),
              onSubmit && submitText && (
                <Button
                  key="submit"
                  disabled={!allowSubmit}
                  loading={submitLoading}
                  size={submitBtnSize}
                  type="primary"
                  onClick={onSubmit}
                >
                  {submitText}
                </Button>
              ),
            ].filter(Boolean)
      }
      centered
      closable={closable}
    >
      {children}
    </AntdModal>
  );
};

export default Modal;
